// app/static/frontend/projects-new/list/components/List.jsx

// https://bvaughn.github.io/react-virtualized/#/components/List
// https://bvaughn.github.io/react-virtualized/#/components/InfiniteLoader
// https://bvaughn.github.io/react-virtualized/#/components/AutoSizer
// https://bvaughn.github.io/react-virtualized/#/components/CellMeasurer

// https://github.com/bvaughn/react-virtualized/blob/master/docs/List.md
// https://github.com/bvaughn/react-virtualized/blob/master/docs/InfiniteLoader.md
// https://github.com/bvaughn/react-virtualized/blob/master/docs/AutoSizer.md
// https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md

// https://github.com/bvaughn/react-virtualized/blob/master/source/List/List.example.js
// https://github.com/bvaughn/react-virtualized/blob/master/source/InfiniteLoader/InfiniteLoader.example.js
// https://github.com/bvaughn/react-virtualized/blob/master/source/AutoSizer/AutoSizer.example.js
// https://github.com/bvaughn/react-virtualized/blob/master/source/CellMeasurer/CellMeasurer.example.js

// https://blog.logrocket.com/rendering-large-lists-with-react-virtualized-82741907a6b3/
// https://embed.plnkr.co/g1dph9/
// https://dev.to/akshaykumar6/building-high-performance-infinite-lists-in-react-31k7

import React from "react"
import PropTypes from "prop-types"
import debounce from "lodash.debounce"
import { AutoSizer, CellMeasurer, CellMeasurerCache, InfiniteLoader, List } from "react-virtualized"

// components
import { ListWidget as StyledListWidget, ListItem as StyledListItem, Widget as StyledWidget } from "quorumdesign"

import StyledListWidgetContainer from "app/static/frontend/widgets/components/ListWidget/style"

// selectors
import {
    getListWidgetData,
    getListWidgetDataEmpty,
    getListWidgetDehydrateExtra,
    getListWidgetFallback,
    getListWidgetHeaderIcons,
    selectListWidgetCountLabel,
} from "app/static/frontend/widgets/selectors/listWidgetSelectors"

// helpers
import { parseHtmlToReact } from "app/static/frontend/imports/desktopHelperFunctions"
import { getWidgetTransactionPermissions } from "shared/pac/permissionHelpers"

// constants
const STATUS_LOADING = 1
const STATUS_LOADED = 2

export class ListWidget extends React.PureComponent {
    constructor(props) {
        super(props)

        // immutable Component-level constant does not belong in state
        this.cache = new CellMeasurerCache({
            defaultHeight: 50,
            fixedWidth: true,
            // "Normally, every cell gets measured individually and is very slow.
            // However, with the keyMapper prop we can specify a constant return value and
            // tell CellMeasurer that all measurements after the first one will hit the
            // cache and we get a speedy solution."
            // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#using-cellmeasurer-with-equal-dynamic-cell-heights-or-widths
            // keyMapper: () => 1,
        })
        this.list = React.createRef()
        this.state = {
            calendarEvents: {},
            count: 0,
            data: [],
            isDownloading: false,
            loadedRowCount: 0,
            loadedRowsMap: {},
            loadingRowCount: 0,
            additionalFilters: this.props.widget.data_sources?.[0]?.additional_filters,
        }
        this._isMounted = false
    }

    componentDidMount() {
        // avoid memory leaks
        // https://stackoverflow.com/a/52061655/6201291
        this._isMounted = true
        this.loadInitialCount()
        window.addEventListener("resize", this.onResize)
        window.addEventListener("ListWidgetRememo", this.onResize)
        this.onResize()
    }

    // this will only ever occur due to changes made in the WidgetEditForm ListWidgetSidebar
    componentDidUpdate(prevProps) {
        if (this.props.qdt && prevProps.qdt && this.props.qdt.value !== prevProps.qdt.value) {
            this.setState({
                count: 0,
                countLabelLoading: undefined,
                data: [],
                isDownloading: false,
                loadedRowCount: 0,
                loadedRowsMap: {},
                loadingRowCount: 0,
            })
        }

        if (
            this.props.dataSources.length &&
            prevProps.dataSources.length &&
            !this.state.isLoading &&
            JSON.stringify(this.props.dataSources) !== JSON.stringify(prevProps.dataSources)
        ) {
            this.loadInitialCount()
        }
    }

    componentWillUnmount() {
        // avoid memory leaks
        // https://stackoverflow.com/a/52061655/6201291
        this._isMounted = false
        window.removeEventListener("resize", this.onResize)
        window.removeEventListener("ListWidgetRememo", this.onResize)
    }

    // CellMeasurer passes a measure() function which needs to be called
    // whenever the contents of the ListWidget shift,
    // like when we resize the browser window (Dashboard.jsx, WidgetEditForm.jsx)
    // or resize the ListWidget (Dashboard.jsx)
    onResize = debounce(() => {
        if (
            this._isMounted &&
            // if canLoad is false, that means that we have been given an invalid widget schema
            // and react-virtualized has not been initialized, so there is no reason to call
            // its lifecycle methods
            this.props.canLoad
        ) {
            // "Reset cached measurements for all cells.
            // This method should be called when a Grid, List or Table needs to reflow content due to a resizing event for a responsive layout
            // (eg. a window width resize may have an impact on the height of cells)."
            // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#clearall-
            this.cache.clearAll()

            // "tl;dr is you have to explicitly update the cache and call recompute yourself :/"
            // https://github.com/bvaughn/react-virtualized/issues/1202#issuecomment-433922116
            this.list.recomputeRowHeights()
        }
    }, 250)

    loadInitialCount() {
        if (this.props.canLoad) {
            this.setState({
                countLabelLoading: "Loading...",
                isLoading: true,
            })

            // grab the count
            // TODO: we can probably get the count/estimated_count from
            // the response of the initial request through the widgetEngine?
            this.props
                .evaluateWidget({
                    ...this.props.widget,
                    // convert the DashboardListWidget to a DashboardNumberWidget
                    // so we can get the QuerySet count
                    data_sources: this.props.dataSources.map((item) => ({
                        ...item,
                        ...(this.state.additionalFilters ? { additional_filters: this.state.additionalFilters } : {}),
                        true_count: true,
                    })),
                })
                .then((response) => {
                    // prevent "Can't perform a React state update on an unmounted component" error
                    if (this._isMounted) {
                        this.setState({
                            calendarEvents: {},
                            count: response.data,
                            countLabelLoading: undefined,
                            data: [],
                            isLoading: false,
                            loadedRowCount: 0,
                            loadedRowsMap: {},
                            loadingRowCount: 0,
                        })
                    }
                })
                .catch(() => {
                    this.setState({
                        countLabelLoading: "Error!",
                    })
                })
        }
    }

    isRowLoaded = ({ index }) =>
        // "InfiniteLoader is not a stateful component, meaning that it does not keep track of which rows it has requested to be loaded.
        // Your component will need to track this information yourself to avoid loading rows multiple times."
        // https://github.com/bvaughn/react-virtualized/blob/master/docs/InfiniteLoader.md#edge-cases-and-considerations
        // STATUS_LOADING or STATUS_LOADED
        Boolean(this.state.loadedRowsMap[index])

    getNextState = (listItem, index, updatedState) => {
        const newData = [...updatedState.data]
        if (this.props.isCalendarEvent) {
            const dayKey = moment(listItem.searchable_date).format("ddd, MMM, Do")
            const newCalendarEvents = { ...updatedState.calendarEvents }

            if (newCalendarEvents[dayKey]) {
                newData[index] = { ...listItem, dayKey, shouldRender: false }
                newCalendarEvents[dayKey].push(listItem)
            } else {
                newData[index] = { ...listItem, dayKey, shouldRender: true }
                newCalendarEvents[dayKey] = [listItem]
            }

            return {
                data: newData,
                calendarEvents: newCalendarEvents,
                loadedRowsMap: {
                    ...updatedState.loadedRowsMap,
                    [index]: STATUS_LOADED,
                },
            }
        }

        newData[index] = listItem

        return {
            data: newData,
            loadedRowsMap: {
                ...updatedState.loadedRowsMap,
                [index]: STATUS_LOADED,
            },
        }
    }

    loadMoreRows = async ({ startIndex, stopIndex }) => {
        const increment = stopIndex - startIndex + 1

        for (let i = startIndex; i <= stopIndex; i += 1) {
            this.state.loadedRowsMap[i] = STATUS_LOADING
        }

        this.setState({
            loadingRowCount: this.state.loadingRowCount + increment,
        })

        const dehydrateExtras = getListWidgetDehydrateExtra({
            qdt: this.props.qdt,
        })

        // grab the evaluated list_widget data from the WidgetEngine
        // (also pass optional additionalFilters in case we are searching in the ListWidget Input bar)
        await this.props
            .evaluateWidget({
                ...this.props.widget,
                data_sources: this.props.dataSources.map((item) => ({
                    ...item,
                    ...(this.state.additionalFilters ? { additional_filters: this.state.additionalFilters } : {}),
                    limit: stopIndex + 1,
                    offset: startIndex,
                })),
                ...(dehydrateExtras
                    ? // matches dehydrate_extra?:dehydrate_extra schema in app/widgets/types.py ListWidget
                      { dehydrate_extra: dehydrateExtras }
                    : {}),
            })
            .then((response) => {
                // prevent "Can't perform a React state update on an unmounted component" error
                if (this._isMounted) {
                    // edge case:
                    // count: 14
                    // startIndex: 0, stopIndex: 13,
                    // startIndex: 13, stopIndex: 13
                    let updatedState = { ...this.state }
                    if (startIndex === stopIndex) {
                        if (response.data) {
                            updatedState = this.getNextState(response.data[0], startIndex, updatedState)
                        }
                    } else {
                        for (let i = startIndex, j = 0; i <= stopIndex; i += 1, j += 1) {
                            if (response.data[j]) {
                                updatedState = this.getNextState(response.data[j], i, updatedState)
                            }
                        }
                    }
                    this.setState({
                        ...updatedState,
                        loadingRowCount: this.state.loadingRowCount - increment,
                        loadedRowCount: this.state.loadedRowCount + increment,
                    })
                }
            })
    }

    rowRenderer = ({ index, key, parent, style }) => (
        // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#using-cellmeasurer-with-images
        <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
            {
                // registerChild is used to
                // "Specify DOM element to be measured, can be used as a ref
                // (by default WindowScroller uses ReactDOM.findDOMNode function)."
                // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#render-props
                ({ measure, registerChild }) => (
                    // 'style' attribute required to position cell (within parent List)
                    <div ref={registerChild} style={style}>
                        {
                            // if the datum has not loaded or is currently loading...
                            !this.state.loadedRowsMap[index] || this.state.loadedRowsMap[index] === STATUS_LOADING ? (
                                // render the skeleton
                                <StyledListItem
                                    // necessary to get the icon and iconFamily for the empty skeleton
                                    datum={getListWidgetDataEmpty({
                                        qdt: this.props.qdt,
                                    })}
                                    // render ListItem skeleton since we don't have a datum to pass yet
                                    empty
                                    // "Perform the cell measurements."
                                    // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#render-props
                                    measure={measure}
                                />
                            ) : (
                                // render the full ListItem
                                <StyledListItem
                                    datum={getListWidgetData({
                                        calendarEvent: this.state.calendarEvents[this.state.data[index].dayKey],
                                        datum: this.state.data[index],
                                        filters: this.props.filters,
                                        isCalendarEvent: this.props.isCalendarEvent,
                                        isExternal: this.props.isExternal,
                                        qdt: this.props.qdt,
                                        segueToProfile: this.props.segueToProfile,
                                        segueToSearch: this.props.segueToSearch,
                                    })}
                                    shouldRender={this.state.data[index]?.shouldRender}
                                    // to render multiple event in one ListItem
                                    hasMultipleFirstLines={this.props.isCalendarEvent}
                                    // needed to disable the pointer cursor on hover in the WidgetEditForm
                                    // and to disable onClick and remove hover state
                                    editing={this.props.editing || this.props.editForm}
                                    isExternal={this.props.isExternal}
                                    // "Perform the cell measurements."
                                    // https://github.com/bvaughn/react-virtualized/blob/master/docs/CellMeasurer.md#render-props
                                    measure={measure}
                                />
                            )
                        }
                    </div>
                )
            }
        </CellMeasurer>
    )

    render() {
        // Get widget transaction permissions using the centralized helper
        const { isForbidden } = getWidgetTransactionPermissions(this.props.qdt)

        return (
            <StyledWidget
                className={this.props.className}
                // count of evaluated widget schema
                count={this.state.count}
                countHasFilters={Boolean(this.props.filters.length)}
                countLabel={selectListWidgetCountLabel({
                    count: this.state.count,
                    qdt: this.props.qdt,
                    isCalendarEvent: this.props.isCalendarEvent,
                })}
                countLabelLoading={this.state.countLabelLoading}
                description={
                    // helper function that will convert a raw HTML string -> DOM Nodes -> React JSX
                    this.props.description && parseHtmlToReact(this.props.description, this.props.widgetKey, false)
                }
                editing={this.props.editing}
                emptyPlaceholder={this.props.emptyPlaceholder}
                forbiddenMessage="You cannot view transaction widgets"
                headerTitle={this.props.headerTitle}
                // this is where we override the global defaults which are defined in
                // app/static/frontend/dashboards/components/Dashboard
                headerIcons={
                    !this.props.editForm &&
                    getListWidgetHeaderIcons({
                        canLoad: this.props.canLoad,
                        dashboardId: this.props.dashboardId,
                        dataSources: this.props.dataSources,
                        editing: this.props.editing,
                        externalDownloadsDisabled: this.props.externalDownloadsDisabled,
                        headerIcons: this.props.headerIcons,
                        isExternal: this.props.isExternal,
                        showInSearch: this.props.showInSearch,
                        widgetKey: this.props.widgetKey,
                        // csv download
                        additionalFilters: this.state.additionalFilters,
                        dehydrateExtras: getListWidgetDehydrateExtra({
                            qdt: this.props.qdt,
                        }),
                        evaluateWidget: this.props.evaluateWidget,
                        isDownloading: this.state.isDownloading,
                        setIsDownloading: (isDownloading) =>
                            this.setState({
                                isDownloading,
                            }),
                        widget: this.props.widget,
                    })
                }
                id={this.props.widgetKey && `widget-${this.props.widgetKey}`}
                isForbidden={isForbidden}
                // whether or not we will absolutely position the Widget Header
                noHeader={!this.props.headerTitle}
                // we want to show a count even if nothing has been selected yet
                // if count is 0, this will be true and we will also show the magic icon
                // if count exists, then this will be false and we will show the regular count iff it exists
                showInitialCount={!this.state.count}
                widget={
                    this.props.canLoad && (
                        <StyledListWidget
                            // needed to disable onClick and remove hover state
                            editing={this.props.editing || this.props.editForm}
                            externalDownloadsDisabled={this.props.externalDownloadsDisabled}
                            isExternal={this.props.isExternal}
                            listItems={
                                // Since StyledListWidget is a flexbox parent,
                                // we need to wrap the AutoSizer in a block element (<div>) as described below

                                // "One word of caution about using AutoSizer with flexbox containers.
                                // Flex containers don't prevent their children from growing and AutoSizer greedily grows to fill as much space as possible.
                                // Combining the two can cause a loop.
                                // The simple way to fix this is to nest AutoSizer inside of a block element (like a <div>) rather than putting it as a direct child of the flex container.
                                // Read more about common AutoSizer questions here."
                                // https://github.com/bvaughn/react-virtualized/blob/master/docs/AutoSizer.md#examples

                                // "When using an AutoSizer as a direct child of a flex box
                                // it usually works out best to wrap it with a div, like so:"
                                // https://github.com/bvaughn/react-virtualized/blob/master/docs/usingAutoSizer.md#can-i-use-autosizer-within-a-flex-container
                                <StyledListWidgetContainer>
                                    <InfiniteLoader
                                        isRowLoaded={this.isRowLoaded}
                                        loadMoreRows={this.loadMoreRows}
                                        minimumBatchSize={20}
                                        rowCount={this.state.count}
                                        threshold={10}
                                    >
                                        {({ onRowsRendered, registerChild }) => (
                                            // https://github.com/bvaughn/react-virtualized/blob/master/docs/usingAutoSizer.md#can-i-use-autosizer-with-other-hocs-like-infiniteloader
                                            <AutoSizer>
                                                {({ height, width }) => {
                                                    if (height) {
                                                        this.setState({ initialHeight: height })
                                                    }

                                                    return (
                                                        <List
                                                            deferredMeasurementCache={this.cache}
                                                            height={height || this.state.initialHeight}
                                                            onRowsRendered={onRowsRendered}
                                                            ref={(element) => {
                                                                // it is necessary to set a ref on the List
                                                                // since we have to explicitly update the cache
                                                                // and reflow the content whenever a resize event occurs
                                                                // https://github.com/bvaughn/react-virtualized/issues/1202#issuecomment-433922116
                                                                this.list = element
                                                                // "This function should be set as the child's ref property.
                                                                // It enables a set of rows to be refreshed once their data has finished loading."
                                                                // https://github.com/bvaughn/react-virtualized/blob/master/docs/InfiniteLoader.md#children-function
                                                                registerChild(element)
                                                            }}
                                                            rowCount={this.state.count}
                                                            rowHeight={this.cache.rowHeight}
                                                            rowRenderer={this.rowRenderer}
                                                            width={width}
                                                            // Add these props to maintain scroll position
                                                            scrollToAlignment="start"
                                                            preserveScrollPosition={true}
                                                        />
                                                    )
                                                }}
                                            </AutoSizer>
                                        )}
                                    </InfiniteLoader>
                                </StyledListWidgetContainer>
                            }
                            // needed for Button onClick and Input onKeyDown (QuorumDesign Input atom)
                            listWidgetInputOnClick={(event, value) => {
                                // if we have not infinitely scrolled, load the search
                                if (!this.state.isLoadingMore) {
                                    this.setState(
                                        {
                                            additionalFilters: value
                                                ? {
                                                      ...this.state.additionalFilters,
                                                      advanced_search: value,
                                                  }
                                                : this.state.additionalFilters,
                                            count: 0,
                                            countLabelLoading: undefined,
                                            data: [],
                                            isDownloading: false,
                                            loadedRowCount: 0,
                                            loadedRowsMap: {},
                                            loadingRowCount: 0,
                                        },
                                        // once we have updated the state, fire the next request
                                        () => {
                                            this.loadInitialCount()
                                            // necessary for the case where the user clicks Submit
                                            // without entering a value in the "Filter this list..." input
                                            // (without this, loadMoreRows does not fire which means that the Widget is permanently "Loading")
                                            this.cache.clearAll()
                                            this.list.recomputeRowHeights()
                                        },
                                    )
                                }
                            }}
                            // we need this to determine when we need to add padding to the Input
                            // so it doens't overlap with the absolutely positioned Header
                            // we don't pass this when we have a description because in that case
                            // the input should be its full width
                            noHeader={!this.props.headerTitle && !this.props.description}
                        />
                    )
                }
                widgetContentIsLoading={this.state.isLoading}
                widgetFallback={getListWidgetFallback({
                    canLoad: this.props.canLoad,
                    data: this.state.data,
                    isLoading: this.state.isLoading,
                })}
            />
        )
    }
}

ListWidget.defaultProps = {}

ListWidget.propTypes = {
    // from app/static/frontend/widgets/components/Widget.jsx
    canLoad: PropTypes.bool,
    className: PropTypes.string,
    dashboardId: PropTypes.string,
    dataSources: PropTypes.array,
    description: PropTypes.string,
    editForm: PropTypes.bool,
    editing: PropTypes.bool,
    emptyPlaceholder: PropTypes.string,
    externalDownloadsDisabled: PropTypes.bool,
    filters: PropTypes.array,
    headerIcons: PropTypes.array,
    headerTitle: PropTypes.string,
    isExternal: PropTypes.bool,
    // needed to segue to the correct profile and display label for count
    qdt: PropTypes.object,
    widget: PropTypes.object,
    widgetKey: PropTypes.string,

    // from app/static/frontend/widgets/containers/ListWidget
    // action creators
    evaluateWidget: PropTypes.func,
    segueToProfile: PropTypes.func,
    segueToSearch: PropTypes.func,
    showInSearch: PropTypes.func,
}

export default ListWidget
